import { create } from 'zustand';
import type {
  RoleDto,
  PermissionDto,
  PermissionHistoryDto,
  UserWithRolesDto,
  UserRoleHistoryDto,
  PermissionsByModule,
} from '../types';
import { roleService, permissionService, userRoleService } from '../services';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

// Module code to display name mapping
const MODULE_NAMES: Record<string, string> = {
  MODM: 'Module Management',
  AUTM: 'Authorization Management',
  USEM: 'User Management',
  ISSE: 'Issuance',
};

interface AuthorizationState {
  // Tab 1: Role Management
  roles: RoleDto[];
  isLoadingRoles: boolean;
  rolesError: string | null;

  // Tab 2: Role Authorizations
  permissions: PermissionDto[];
  permissionsByModule: PermissionsByModule[];
  selectedRoleForAuth: RoleDto | null;
  pendingPermissionChanges: Record<string, boolean>; // permissionCode -> granted
  isSavingPermissions: boolean;
  isLoadingPermissions: boolean;
  permissionsError: string | null;
  permissionHistoryModalOpen: boolean;
  permissionHistory: PermissionHistoryDto[];
  isLoadingPermissionHistory: boolean;

  // Tab 3: User Roles
  usersWithRoles: UserWithRolesDto[];
  selectedUserIds: number[];
  isLoadingUsers: boolean;
  usersError: string | null;
  userRoleHistoryModalOpen: boolean;
  userRoleHistory: UserRoleHistoryDto[];
  selectedUserForHistory: UserWithRolesDto | null;
  isLoadingUserRoleHistory: boolean;

  // User Role Sheet (single user role editing)
  userRoleSheetOpen: boolean;
  selectedUserForRoleEdit: UserWithRolesDto | null;
  pendingUserRoleChanges: Record<number, boolean>; // roleId -> should be assigned
  isSubmittingUserRoles: boolean;
  userRoleSubmitError: string | null;

  // Batch role assignment
  batchSelectedRoleIds: number[];
  isApplyingBatchRoles: boolean;

  // Tab 1 Actions
  fetchRoles: () => Promise<void>;

  // Tab 2 Actions
  fetchPermissions: () => Promise<void>;
  selectRoleForAuth: (role: RoleDto) => void;
  clearRoleForAuth: () => void;
  togglePermission: (permissionCode: string) => void;
  discardPermissionChanges: () => void;
  savePermissionChanges: () => Promise<boolean>;
  hasPendingChanges: () => boolean;
  getEffectivePermissions: () => string[];
  openPermissionHistoryModal: () => void;
  closePermissionHistoryModal: () => void;
  fetchPermissionHistory: (roleId: number) => Promise<void>;

  // Tab 3 Actions
  fetchUsersWithRoles: () => Promise<void>;
  toggleUserSelection: (userId: number) => void;
  selectAllUsers: () => void;
  clearUserSelection: () => void;
  openUserRoleHistoryModal: (user: UserWithRolesDto) => void;
  closeUserRoleHistoryModal: () => void;
  fetchUserRoleHistory: (userId: number) => Promise<void>;

  // User Role Sheet Actions
  openUserRoleSheet: (user: UserWithRolesDto) => void;
  closeUserRoleSheet: () => void;
  toggleUserRole: (roleId: number) => void;
  discardUserRoleChanges: () => void;
  hasUserRolePendingChanges: () => boolean;
  getEffectiveUserRoles: () => number[];
  saveUserRoles: () => Promise<boolean>;

  // Batch Role Assignment Actions
  toggleBatchRoleSelection: (roleId: number) => void;
  clearBatchRoleSelection: () => void;
  applyBatchRoles: () => Promise<boolean>;
}

export const useAuthorizationStore = create<AuthorizationState>()((set, get) => ({
  // Initial State - Tab 1
  roles: [],
  isLoadingRoles: false,
  rolesError: null,

  // Initial State - Tab 2
  permissions: [],
  permissionsByModule: [],
  selectedRoleForAuth: null,
  pendingPermissionChanges: {},
  isSavingPermissions: false,
  isLoadingPermissions: false,
  permissionsError: null,
  permissionHistoryModalOpen: false,
  permissionHistory: [],
  isLoadingPermissionHistory: false,

  // Initial State - Tab 3
  usersWithRoles: [],
  selectedUserIds: [],
  isLoadingUsers: false,
  usersError: null,
  userRoleHistoryModalOpen: false,
  userRoleHistory: [],
  selectedUserForHistory: null,
  isLoadingUserRoleHistory: false,

  // Initial State - User Role Sheet
  userRoleSheetOpen: false,
  selectedUserForRoleEdit: null,
  pendingUserRoleChanges: {},
  isSubmittingUserRoles: false,
  userRoleSubmitError: null,

  // Initial State - Batch Role Assignment
  batchSelectedRoleIds: [],
  isApplyingBatchRoles: false,

  // Tab 1 Actions
  fetchRoles: async () => {
    set({ isLoadingRoles: true, rolesError: null });
    try {
      const roles = await roleService.getAllRoles();
      set({ roles, isLoadingRoles: false });
    } catch (error) {
      set({
        rolesError: getApiErrorMessage(error, 'Failed to fetch roles'),
        isLoadingRoles: false,
      });
    }
  },

  // Tab 2 Actions
  fetchPermissions: async () => {
    set({ isLoadingPermissions: true, permissionsError: null });
    try {
      const permissions = await permissionService.getAllPermissions();

      // Group permissions by module
      const moduleMap = new Map<string, PermissionDto[]>();
      permissions.forEach((p) => {
        const existing = moduleMap.get(p.moduleCode) || [];
        moduleMap.set(p.moduleCode, [...existing, p]);
      });

      const permissionsByModule: PermissionsByModule[] = Array.from(moduleMap.entries()).map(
        ([moduleCode, perms]) => ({
          moduleCode,
          moduleName: MODULE_NAMES[moduleCode] || moduleCode,
          permissions: perms,
        })
      );

      set({ permissions, permissionsByModule, isLoadingPermissions: false });
    } catch (error) {
      set({
        permissionsError: getApiErrorMessage(error, 'Failed to fetch permissions'),
        isLoadingPermissions: false,
      });
    }
  },

  selectRoleForAuth: (role: RoleDto) => {
    set({
      selectedRoleForAuth: role,
      pendingPermissionChanges: {},
    });
  },

  clearRoleForAuth: () => {
    set({
      selectedRoleForAuth: null,
      pendingPermissionChanges: {},
    });
  },

  togglePermission: (permissionCode: string) => {
    const { selectedRoleForAuth, pendingPermissionChanges } = get();
    if (!selectedRoleForAuth) return;

    const originalGranted = selectedRoleForAuth.permissions.includes(permissionCode);
    const pendingValue = pendingPermissionChanges[permissionCode];

    // Determine current effective value
    const currentValue = pendingValue !== undefined ? pendingValue : originalGranted;
    const newValue = !currentValue;

    // If toggling back to original, remove from pending changes
    if (newValue === originalGranted) {
      const rest = { ...pendingPermissionChanges };
      delete rest[permissionCode];
      set({ pendingPermissionChanges: rest });
    } else {
      set({
        pendingPermissionChanges: {
          ...pendingPermissionChanges,
          [permissionCode]: newValue,
        },
      });
    }
  },

  discardPermissionChanges: () => {
    set({ pendingPermissionChanges: {} });
  },

  savePermissionChanges: async () => {
    const { selectedRoleForAuth } = get();
    if (!selectedRoleForAuth) return false;

    const effectivePermissions = get().getEffectivePermissions();
    set({ isSavingPermissions: true });
    try {
      const updatedRole = await roleService.updateRolePermissions(
        selectedRoleForAuth.id,
        effectivePermissions
      );
      // Update the selected role with the server response and clear pending changes
      set({
        selectedRoleForAuth: updatedRole,
        pendingPermissionChanges: {},
        isSavingPermissions: false,
      });
      // Also refresh the roles list so the Role Management tab stays in sync
      get().fetchRoles();
      return true;
    } catch (error) {
      set({
        permissionsError: getApiErrorMessage(error, 'Failed to save permissions'),
        isSavingPermissions: false,
      });
      return false;
    }
  },

  hasPendingChanges: () => {
    return Object.keys(get().pendingPermissionChanges).length > 0;
  },

  getEffectivePermissions: () => {
    const { selectedRoleForAuth, pendingPermissionChanges } = get();
    if (!selectedRoleForAuth) return [];

    const effectiveSet = new Set(selectedRoleForAuth.permissions);

    // Apply pending changes
    Object.entries(pendingPermissionChanges).forEach(([code, granted]) => {
      if (granted) {
        effectiveSet.add(code);
      } else {
        effectiveSet.delete(code);
      }
    });

    return Array.from(effectiveSet);
  },

  openPermissionHistoryModal: () => {
    const { selectedRoleForAuth } = get();
    if (selectedRoleForAuth) {
      set({ permissionHistoryModalOpen: true });
      get().fetchPermissionHistory(selectedRoleForAuth.id);
    }
  },

  closePermissionHistoryModal: () => {
    set({
      permissionHistoryModalOpen: false,
      permissionHistory: [],
    });
  },

  fetchPermissionHistory: async (roleId: number) => {
    set({ isLoadingPermissionHistory: true });
    try {
      const permissionHistory = await permissionService.getPermissionHistory(roleId);
      set({ permissionHistory, isLoadingPermissionHistory: false });
    } catch {
      set({ isLoadingPermissionHistory: false });
    }
  },

  // Tab 3 Actions
  fetchUsersWithRoles: async () => {
    set({ isLoadingUsers: true, usersError: null });
    try {
      const usersWithRoles = await userRoleService.getUsersWithRoles();
      set({ usersWithRoles, isLoadingUsers: false });
    } catch (error) {
      set({
        usersError: getApiErrorMessage(error, 'Failed to fetch users'),
        isLoadingUsers: false,
      });
    }
  },

  toggleUserSelection: (userId: number) => {
    const { selectedUserIds } = get();
    if (selectedUserIds.includes(userId)) {
      set({ selectedUserIds: selectedUserIds.filter((id) => id !== userId) });
    } else {
      set({ selectedUserIds: [...selectedUserIds, userId] });
    }
  },

  selectAllUsers: () => {
    const { usersWithRoles } = get();
    set({ selectedUserIds: usersWithRoles.map((u) => u.userId) });
  },

  clearUserSelection: () => {
    set({ selectedUserIds: [] });
  },

  openUserRoleHistoryModal: (user: UserWithRolesDto) => {
    set({
      userRoleHistoryModalOpen: true,
      selectedUserForHistory: user,
    });
    get().fetchUserRoleHistory(user.userId);
  },

  closeUserRoleHistoryModal: () => {
    set({
      userRoleHistoryModalOpen: false,
      selectedUserForHistory: null,
      userRoleHistory: [],
    });
  },

  fetchUserRoleHistory: async (userId: number) => {
    set({ isLoadingUserRoleHistory: true });
    try {
      const userRoleHistory = await userRoleService.getUserRoleHistory(userId);
      set({ userRoleHistory, isLoadingUserRoleHistory: false });
    } catch {
      set({ isLoadingUserRoleHistory: false });
    }
  },

  // User Role Sheet Actions
  openUserRoleSheet: (user: UserWithRolesDto) => {
    set({
      userRoleSheetOpen: true,
      selectedUserForRoleEdit: user,
      pendingUserRoleChanges: {},
      userRoleSubmitError: null,
    });
  },

  closeUserRoleSheet: () => {
    set({
      userRoleSheetOpen: false,
      selectedUserForRoleEdit: null,
      pendingUserRoleChanges: {},
      userRoleSubmitError: null,
    });
  },

  toggleUserRole: (roleId: number) => {
    const { selectedUserForRoleEdit, pendingUserRoleChanges } = get();
    if (!selectedUserForRoleEdit) return;

    const originalAssigned = selectedUserForRoleEdit.roles.some((r) => r.roleId === roleId);
    const pendingValue = pendingUserRoleChanges[roleId];

    // Determine current effective value
    const currentValue = pendingValue !== undefined ? pendingValue : originalAssigned;
    const newValue = !currentValue;

    // If toggling back to original, remove from pending changes
    if (newValue === originalAssigned) {
      const rest = { ...pendingUserRoleChanges };
      delete rest[roleId];
      set({ pendingUserRoleChanges: rest });
    } else {
      set({
        pendingUserRoleChanges: {
          ...pendingUserRoleChanges,
          [roleId]: newValue,
        },
      });
    }
  },

  discardUserRoleChanges: () => {
    set({ pendingUserRoleChanges: {} });
  },

  hasUserRolePendingChanges: () => {
    return Object.keys(get().pendingUserRoleChanges).length > 0;
  },

  getEffectiveUserRoles: () => {
    const { selectedUserForRoleEdit, pendingUserRoleChanges } = get();
    if (!selectedUserForRoleEdit) return [];

    const effectiveSet = new Set(selectedUserForRoleEdit.roles.map((r) => r.roleId));

    // Apply pending changes
    Object.entries(pendingUserRoleChanges).forEach(([roleIdStr, assigned]) => {
      const roleId = parseInt(roleIdStr, 10);
      if (assigned) {
        effectiveSet.add(roleId);
      } else {
        effectiveSet.delete(roleId);
      }
    });

    return Array.from(effectiveSet);
  },

  saveUserRoles: async () => {
    const { selectedUserForRoleEdit } = get();
    if (!selectedUserForRoleEdit) return false;

    const effectiveRoleIds = get().getEffectiveUserRoles();
    set({ isSubmittingUserRoles: true, userRoleSubmitError: null });
    try {
      await userRoleService.updateUserRoles(selectedUserForRoleEdit.userId, effectiveRoleIds);
      set({ isSubmittingUserRoles: false });
      get().closeUserRoleSheet();
      get().fetchUsersWithRoles();
      return true;
    } catch (error) {
      set({
        userRoleSubmitError: getApiErrorMessage(error, 'Failed to update user roles'),
        isSubmittingUserRoles: false,
      });
      return false;
    }
  },

  // Batch Role Assignment Actions
  toggleBatchRoleSelection: (roleId: number) => {
    const { batchSelectedRoleIds } = get();
    if (batchSelectedRoleIds.includes(roleId)) {
      set({ batchSelectedRoleIds: batchSelectedRoleIds.filter((id) => id !== roleId) });
    } else {
      set({ batchSelectedRoleIds: [...batchSelectedRoleIds, roleId] });
    }
  },

  clearBatchRoleSelection: () => {
    set({ batchSelectedRoleIds: [] });
  },

  applyBatchRoles: async () => {
    const { selectedUserIds, batchSelectedRoleIds, usersWithRoles } = get();
    if (selectedUserIds.length === 0 || batchSelectedRoleIds.length === 0) return false;

    set({ isApplyingBatchRoles: true });
    try {
      // For each selected user, merge their existing roles with the batch-selected roles
      for (const userId of selectedUserIds) {
        const user = usersWithRoles.find((u) => u.userId === userId);
        const existingRoleIds = user ? user.roles.map((r) => r.roleId) : [];
        const mergedRoleIds = [...new Set([...existingRoleIds, ...batchSelectedRoleIds])];
        await userRoleService.updateUserRoles(userId, mergedRoleIds);
      }
      set({ isApplyingBatchRoles: false });
      get().clearBatchRoleSelection();
      get().clearUserSelection();
      get().fetchUsersWithRoles();
      return true;
    } catch (error) {
      set({ isApplyingBatchRoles: false });
      throw error;
    }
  },
}));
